<template>
	<div class="container py-4">
		<!-- Header -->
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration/integration"
					title="Back to integrations"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">MapVision</h1>
					<p class="text-muted mb-0">Configure the MapVision integration</p>
				</div>
			</div>
			<ButtonLoading
				text="Sync"
				icon="bi bi-arrow-repeat"
				variant="primary"
				:loading="syncing"
				:disabled="loading || !hasConfig"
				@click="sync"
			/>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else>
			<div class="card mb-4 integration-card">
				<div
					class="card-header d-flex justify-content-between align-items-center"
				>
					<div>
						<h2 class="h5 mb-0">Connection</h2>
					</div>
					<ButtonLoading
						text="Save"
						icon="bi bi-save"
						:loading="saving"
						:disabled="loading"
						@click="save"
					/>
				</div>
				<div class="card-body">
					<form @submit.prevent="save">
						<div class="row g-3">
							<div class="col-md-12">
								<label class="form-label" for="mapvision-base-url">
									Base URL
								</label>
								<input
									id="mapvision-base-url"
									v-model="form.baseUrl"
									type="url"
									class="form-control"
									placeholder="https://mapvision.example.com"
									autocomplete="url"
									required
								/>
								<div class="form-text">
									The root URL of your MapVision instance.
								</div>
							</div>

							<div class="col-md-6">
								<label class="form-label" for="mapvision-username">
									Username
								</label>
								<input
									id="mapvision-username"
									v-model="form.username"
									type="text"
									class="form-control"
									autocomplete="username"
									required
								/>
							</div>

							<div class="col-md-6">
								<label class="form-label" for="mapvision-password">
									Password
								</label>
								<input
									id="mapvision-password"
									v-model="form.password"
									type="password"
									class="form-control"
									:placeholder="
										passwordConfigured
											? 'Leave blank to keep current password'
											: 'Enter password'
									"
									autocomplete="new-password"
									:required="!passwordConfigured"
								/>
								<div class="form-text">
									{{
										passwordConfigured
											? "A password is currently stored."
											: "No password is currently stored."
									}}
								</div>
							</div>
						</div>
					</form>
				</div>
			</div>
		</template>

		<div v-if="hasConfig" class="card mb-4 integration-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<div>
					<h2 class="h5 mb-0">Synchronization</h2>
				</div>
				<button
					class="btn btn-outline-secondary btn-sm"
					type="button"
					:disabled="statusLoading"
					title="Refresh the mirror's status"
					@click="loadStatus"
				>
					<i class="bi bi-arrow-clockwise"></i>
					Refresh
				</button>
			</div>
			<div class="card-body">
				<div v-if="statusError" class="alert alert-danger mb-0" role="alert">
					{{ statusError }}
				</div>
				<div v-else-if="statusLoading" class="text-muted">
					Loading the mirror's status…
				</div>
				<template v-else-if="syncStatus">
					<div
						v-if="syncStatus.lastRun && !syncStatus.lastRun.succeeded"
						class="alert alert-warning"
						role="alert"
					>
						The last sync did not finish, so the mirror is as of the last
						successful run plus whatever this part-run wrote:
						{{ syncStatus.lastRun.error || "it stopped early." }}
					</div>

					<p v-if="syncStatus.syncedAt" class="mb-1">
						Last synced
						<TimeRelativeWithTooltip :time="syncStatus.syncedAt" />
						<span v-if="syncStatus.running" class="badge text-bg-info ms-2">
							running now
						</span>
					</p>
					<p v-else class="mb-1 text-muted">
						No MapVision sync has completed yet<template
							v-if="syncStatus.running"
						>
							— one is running now</template
						>.
					</p>

					<div v-if="syncStatus.resourceCounts.length" class="mt-4">
						<h3 class="h6 mb-1">Mirrored data</h3>
						<p class="text-muted small mb-2">
							Rows currently mirrored from MapVision, largest first.
							<strong>Snapshot</strong> tables come from the bulk export and are
							not refreshed by the live sync.
						</p>
						<div class="table-responsive resource-counts">
							<table class="table table-sm align-middle mb-0">
								<thead>
									<tr>
										<th scope="col">Table</th>
										<th scope="col">Group</th>
										<th scope="col" class="text-end">Rows</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="count in syncStatus.resourceCounts"
										:key="count.resource"
									>
										<td>{{ count.label }}</td>
										<td>
											<span class="badge text-bg-light text-dark">{{
												count.category
											}}</span>
										</td>
										<td class="text-end">
											{{ count.rows.toLocaleString() }}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</template>
			</div>
		</div>

		<ToastNotification
			title="Saved"
			message="MapVision configuration updated."
			:show="saved"
		/>
		<ToastNotification
			title="Sync started"
			message="MapVision sync job queued."
			:show="syncSaved"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { MapvisionSyncStatus } from "@/type/api";

interface MapVisionConfig {
	baseUrl: string;
	username: string;
	passwordConfigured: boolean;
}

const loading = ref(true);
const saving = ref(false);
const syncing = ref(false);
const error = ref("");
const saved = ref(false);
const syncSaved = ref(false);
const passwordConfigured = ref(false);
const hasConfig = ref(false);
// syncStatus is the mirror's recorded state; null until the read answers. The
// panel is gated on hasConfig, so a page with no integration asks for nothing.
const syncStatus = ref<MapvisionSyncStatus | null>(null);
const statusLoading = ref(false);
const statusError = ref("");

const form = reactive({
	baseUrl: "",
	username: "",
	password: "",
});

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const config = await apiClient.JSONGet<MapVisionConfig>(
			"/api/integration/mapvision",
		);
		form.baseUrl = config.baseUrl;
		form.username = config.username;
		form.password = "";
		passwordConfigured.value = config.passwordConfigured;
		hasConfig.value = config.baseUrl !== "";
	} catch (e: any) {
		error.value = e?.message || "Failed to load MapVision configuration.";
	} finally {
		loading.value = false;
	}
}

async function loadStatus() {
	statusLoading.value = true;
	statusError.value = "";
	try {
		syncStatus.value = await apiClient.JSONGet<MapvisionSyncStatus>(
			"/api/integration/mapvision/sync",
		);
	} catch (e: any) {
		statusError.value = e?.message || "Failed to load the mirror's status.";
	} finally {
		statusLoading.value = false;
	}
}

async function save() {
	saving.value = true;
	error.value = "";
	saved.value = false;
	try {
		const config = await apiClient.JSONPut<MapVisionConfig>(
			"/api/integration/mapvision",
			{
				baseUrl: form.baseUrl,
				username: form.username,
				password: form.password,
			},
		);
		form.baseUrl = config.baseUrl;
		form.username = config.username;
		form.password = "";
		passwordConfigured.value = config.passwordConfigured;
		hasConfig.value = config.baseUrl !== "";
		saved.value = true;
		if (hasConfig.value && !syncStatus.value) {
			await loadStatus();
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to save MapVision configuration.";
	} finally {
		saving.value = false;
	}
}

async function sync() {
	syncing.value = true;
	error.value = "";
	syncSaved.value = false;
	try {
		await apiClient.JSONPost("/api/integration/mapvision/sync", {});
		syncSaved.value = true;
		// The enqueue inserts the job before it answers, so the status read
		// right after shows the run as queued.
		if (hasConfig.value) {
			await loadStatus();
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to start MapVision sync.";
	} finally {
		syncing.value = false;
	}
}

onMounted(async () => {
	await load();
	if (hasConfig.value) {
		await loadStatus();
	}
});
</script>

<style scoped>
.integration-card {
	border-left: 5px solid #0d6efd;
}
</style>
